import { useState } from "react";
import toast from "react-hot-toast";
import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useMutation from "./useMutattion";
import { GET_SIGNED_URL } from "../api/exercises/exercises";

type FileType = "IMAGE" | "VIDEO" | "AUDIO";

interface UseFileUploaderProps {
  acceptedTypes?: string[];
}

interface UseFileUploaderReturn {
  fileLoading: boolean;
  uploadFile: (_file: File, _type: FileType) => Promise<string | null>;
}

const typeMapping: Record<FileType, string[]> = {
  IMAGE: ["image/jpeg", "image/png", "image/gif", "image/svg+xml"],
  VIDEO: ["video/mp4", "video/x-m4v", "video/*", "video/quicktime"],
  AUDIO: ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4", "audio/x-m4a"],
};

export const useFileUploader = ({
  acceptedTypes = [],
}: UseFileUploaderProps): UseFileUploaderReturn => {
  const { t } = useTranslation();
  const [fileLoading, setFileLoading] = useState(false);
  const { execute: getSignedUrl, loading } = useMutation<any>(
    GET_SIGNED_URL,
    "POST",
    {
      autoFetch: false,
    }
  );

  const uploadToSignedUrl = async (file: File, signedUrl: string) => {
    try {
      const res = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      return Number(res.status) === HttpStatusCode.Ok ? signedUrl : null;
    } catch {
      return null;
    } finally {
      setFileLoading(false);
    }
  };

  const uploadFile = async (
    file: File,
    type: FileType
  ): Promise<string | null> => {
    try {
      setFileLoading(true);
      if (!file) return null;

      const typesToCheck = acceptedTypes || typeMapping[type];
      if (typesToCheck.length && !typesToCheck.includes(file.type)) {
        toast.error(t("alert.error.isNotAccessType"));
        return null;
      }

      const res = await getSignedUrl({ contentType: file.type });
      if (!res?.url || !file) return null;

      const url = await uploadToSignedUrl(file, res.url);
      if (!url) {
        toast.error(t("alert.error.upload"));
        return null;
      }

      const parsedUrl = new URL(res.url);
      const pathName = parsedUrl.pathname.split("/");
      return `${parsedUrl.origin}/${pathName[1]}/${res.filePath}`;
    } catch {
      return null;
    } finally {
      setFileLoading(false);
    }
  };

  return {
    fileLoading: fileLoading || loading,
    uploadFile,
  };
};
